import { useMemo } from "react";
import { Button, Dropdown } from "antd";
import {
  MoreHorizontal,
  Eye,
  Upload,
  CheckSquare,
  Pencil,
  Lock,
  Unlock,
  Trash2,
} from "lucide-react";
import type { MenuProps } from "antd";
import styles from "./ActionMenu.module.css";

export interface ActionMenuHandlers<T = any> {
  onView?: (record: T) => void;
  onUpdate?: (record: T) => void;
  onApprove?: (record: T) => void;
  onEdit?: (record: T) => void;
  onManageSession?: (record: T) => void;
  onLock?: (record: T) => void;
  onDelete?: (record: T) => void;
}

export interface ActionMenuProps<T = any> {
  record: T;
  handlers: ActionMenuHandlers<T>;
  className?: string;
  showItems?: Partial<{
    view: boolean;
    update: boolean;
    approve: boolean;
    edit: boolean;
    manageSession: boolean;
    lock: boolean;
    delete: boolean;
  }>;
}

type MenuKey =
  | "view"
  | "update"
  | "approve"
  | "edit"
  | "session"
  | "lock"
  | "delete";

const MENU_CONFIG: Array<{
  key: MenuKey;
  label: string;
  icon: typeof Eye;
  handlerKey: keyof ActionMenuHandlers;
  danger: boolean;
}> = [
  {
    key: "view",
    label: "Xem Chi Tiết",
    icon: Eye,
    handlerKey: "onView",
    danger: false,
  },
  {
    key: "update",
    label: "Cập Nhật Thông Tin",
    icon: Upload,
    handlerKey: "onUpdate",
    danger: false,
  },
    {
    key: "approve",
    label: "Duyệt/Từ Chối",
    icon: CheckSquare,
    handlerKey: "onApprove",
    danger: false,
  },
  {
    key: "edit",
    label: "Chỉnh Sửa Tài Khoản",
    icon: Pencil,
    handlerKey: "onEdit",
    danger: false,
  },
  // {
  //   key: "session",
  //   label: "Quản Lý Session & IP",
  //   icon: Cpu,
  //   handlerKey: "onManageSession",
  //   danger: false,
  // },
  {
    key: "lock",
    label: "Khoá Tài Khoản",
    icon: Lock,
    handlerKey: "onLock",
    danger: true,
  },
  {
    key: "delete",
    label: "Xoá Tài Khoản",
    icon: Trash2,
    handlerKey: "onDelete",
    danger: true,
  },
];

const DEFAULT_SHOW_ITEMS = {
  view: true,
  update: true,
  approve: true,
  edit: true,
  manageSession: true,
  lock: true,
  delete: true,
};

export const ActionMenu = <T,>({
  record,
  handlers,
  className,
  showItems = DEFAULT_SHOW_ITEMS,
}: ActionMenuProps<T>) => {
  const menuItems = useMemo((): MenuProps["items"] => {
    // Kiểm tra xem record có trạng thái khóa không
    const isLocked = (record as any)?.status === "locked";

    return MENU_CONFIG.filter((config) => {
      const showKey = config.key as keyof typeof showItems;
      const handler = handlers[config.handlerKey];
      return showItems[showKey] !== false && handler;
    }).map((config) => {
      // Xử lý đặc biệt cho menu item "lock"
      let label = config.label;
      let Icon = config.icon;
      let isDanger = config.danger;

      if (config.key === "lock") {
        if (isLocked) {
          label = "Mở khóa";
          Icon = Unlock;
          isDanger = false; // Mở khóa không phải là hành động nguy hiểm
        } else {
          label = "Khoá Tài Khoản";
          Icon = Lock;
          isDanger = true; // Khóa tài khoản là hành động nguy hiểm
        }
      }

      return {
        key: config.key,
        label: (
          <span className={styles.menuItemLabel}>
            {label}
            <Icon size={20} className={styles.menuItemIcon} />
          </span>
        ),
        ...(isDanger && { danger: true }),
        onClick: () => handlers[config.handlerKey]?.(record),
      };
    });
  }, [record, handlers, showItems]);

  return (
    <div className={styles.wrapper}>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        overlayClassName={styles.dropdownMenu}
      >
        <Button
          type="text"
          icon={<MoreHorizontal size={40} />}
          className={className || styles.actionMenuButton}
        />
      </Dropdown>
    </div>
  );
};
